import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { AiOutlineClose } from "react-icons/ai";

import { fetchDuplicateUsers, putUserState } from "@/apis/user";
import ModalLayout from "@/components/Layout/ModalLayout";

interface DuplicateUser {
  userId: string;
  userName: string;
  userStateCode: string;
  roleId: string;
  lastLoginDate: string | null;
  createdDate: string;
  telNo: string;
  cmtNm: string | null;
  garNm: string | null;
  leafNm: string | null;
  reservationCount: number;
  recommended: "keep" | "delete";
}

interface DuplicateGroup {
  userName: string;
  telNo: string;
  users: DuplicateUser[];
}

interface Props {
  onClose: () => void;
  onComplete: () => void;
}

const DuplicateUserModal = ({ onClose, onComplete }: Props) => {
  const [groups, setGroups] = useState<DuplicateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadDuplicates();
  }, []);

  const loadDuplicates = async () => {
    setLoading(true);
    const data = await fetchDuplicateUsers();
    setGroups(data);
    // 기본값: 추천 삭제 대상을 미리 선택
    const defaults = new Set<string>();
    for (const group of data) {
      for (const user of group.users) {
        if (user.recommended === "delete") {
          defaults.add(user.userId);
        }
      }
    }
    setDeleteTargets(defaults);
    setLoading(false);
  };

  const toggleTarget = (userId: string) => {
    setDeleteTargets(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleDelete = async () => {
    if (deleteTargets.size === 0) return;
    if (
      !window.confirm(
        `선택한 ${deleteTargets.size}개 계정을 삭제 처리하시겠습니까?`,
      )
    )
      return;

    setProcessing(true);
    try {
      for (const userId of deleteTargets) {
        await putUserState({ userId, userStateCode: "09" });
      }
      alert(`${deleteTargets.size}개 계정이 삭제 처리되었습니다.`);
      onComplete();
      onClose();
    } catch {
      alert("삭제 처리 중 오류가 발생했습니다.");
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";
    return dayjs(date).format("YYYY.MM.DD HH:mm");
  };

  const roleLabel: Record<string, string> = {
    ADMIN: "관리자",
    LEADER: "리더",
    MINISTRY: "사역자",
    USER: "일반",
  };

  return (
    <ModalLayout variant="primary" onClose={processing ? () => {} : onClose}>
      <div className="relative flex w-[90vw] max-w-[700px] flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h3 className="text-base font-bold text-gray-900">
            중복 유저 관리
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
          >
            <AiOutlineClose size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-gray-500">중복 유저 검색 중...</p>
            </div>
          ) : groups.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-gray-500">
                중복된 유저가 없습니다.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-xs text-gray-500">
                이름과 전화번호가 동일한 계정을 표시합니다. 예약 건수와 최근
                로그인 기준으로 삭제 대상이 자동 추천됩니다.
              </p>
              {groups.map(group => (
                <div
                  key={`${group.userName}|${group.telNo}`}
                  className="rounded-lg border border-gray-200 overflow-hidden"
                >
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <span className="text-sm font-semibold text-gray-800">
                      {group.userName}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">
                      {group.telNo}
                    </span>
                    <span className="ml-2 text-xs text-orange-600 font-medium">
                      {group.users.length}개 계정
                    </span>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50/50 text-gray-500">
                        <th className="px-3 py-1.5 text-center w-10">삭제</th>
                        <th className="px-3 py-1.5 text-left">역할</th>
                        <th className="px-3 py-1.5 text-left">소속</th>
                        <th className="px-3 py-1.5 text-center">예약</th>
                        <th className="px-3 py-1.5 text-left">최근 로그인</th>
                        <th className="px-3 py-1.5 text-left">가입일</th>
                        <th className="px-3 py-1.5 text-center">추천</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.users.map(user => {
                        const isTarget = deleteTargets.has(user.userId);
                        return (
                          <tr
                            key={user.userId}
                            className={`border-t border-gray-100 ${
                              isTarget
                                ? "bg-red-50"
                                : user.recommended === "keep"
                                  ? "bg-green-50/30"
                                  : ""
                            }`}
                          >
                            <td className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={isTarget}
                                onChange={() => toggleTarget(user.userId)}
                                className="accent-red-500"
                              />
                            </td>
                            <td className="px-3 py-2">
                              {roleLabel[user.roleId] ?? user.roleId}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {[user.cmtNm, user.garNm, user.leafNm]
                                .filter(Boolean)
                                .join(" > ") || "-"}
                            </td>
                            <td className="px-3 py-2 text-center font-medium">
                              {user.reservationCount}건
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {formatDate(user.lastLoginDate)}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {formatDate(user.createdDate)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {user.recommended === "keep" ? (
                                <span className="inline-block rounded bg-green-100 px-1.5 py-0.5 text-green-700 font-medium">
                                  유지
                                </span>
                              ) : (
                                <span className="inline-block rounded bg-red-100 px-1.5 py-0.5 text-red-600 font-medium">
                                  삭제
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
          <p className="text-xs text-gray-500">
            {deleteTargets.size > 0
              ? `${deleteTargets.size}개 계정 삭제 예정`
              : "삭제할 계정을 선택하세요"}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              닫기
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={processing || deleteTargets.size === 0}
              className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:opacity-50"
            >
              {processing
                ? "처리 중..."
                : `선택 계정 삭제 (${deleteTargets.size}건)`}
            </button>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
};

export default DuplicateUserModal;
